import { PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

export const BillingAuditSchema = z.object({
    id: z.number().int(),
    category: z.string(),
    error: z.string(),
    errorDate: z.string().datetime().nullable(),
    isHidden: z.boolean(),
    isResolved: z.boolean(),
    pipsId: z.number().int()
});

export interface PagedResult<T> {
	data: T[];
	total: number;
}

export type BillingAudit = z.infer<typeof BillingAuditSchema>;
export type BillingAuditTab = 'active' | 'hidden' | 'resolved';

export const all = async (
    globalSearch: string = '',
    tab: BillingAuditTab = 'active',
    tabSearch: string = '',
    startDate?: Date | null,
    endDate?: Date | null,
    sorting?: SortingState,
    pagination: PaginationState = { pageIndex: 0, pageSize: 10 },
    categories: string[] = [],
): Promise<PagedResult<BillingAudit>> => {
    const params = new URLSearchParams();

    if (globalSearch) params.append('globalSearch', globalSearch);
    if (tab) params.append('tab', tab);
    if (tabSearch) params.append('tabSearch', tabSearch);
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());
    categories.forEach((category) => params.append('categories', category));

    params.append('pageIndex', (pagination.pageIndex + 1).toString());
    params.append('pageSize', pagination.pageSize.toString());

    if (sorting && sorting.length > 0) {
        const sort = sorting[0];
        params.append('sortBy', sort.id);
        params.append('sortDirection', sort.desc ? 'desc' : 'asc');
    }
    const response = await fetch(`/BillingAudit/?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Blackline Billing audit data');
    return await response.json();
};

export const getCategoryOptions = async (): Promise<string[]> => {
    const response = await fetch(
        `/BillingAudit/GetCategories`,
    );
    if (!response.ok) throw new Error('Failed to fetch category options');
    return await response.json();
};

export const hideExceptions = async (
    ids: number[],
    hide: boolean,
): Promise<void> => {
    const res = await fetch(`/BillingAudit/HideExceptions`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ exceptionIds: ids, hide }),
    });

    if (!res.ok) {
        let message = 'Failed to update exception visibility';
        try {
            const body = await res.json();
            if (typeof body?.message === 'string' && body.message.trim().length > 0) {
                message = body.message;
            }
        } catch {
            // Keep default message when the response body is not JSON.
        }

        throw new Error(message);
    }
};